import { useState, useEffect } from 'react'
import {
  Grid,
  Typography,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  InputAdornment,
  useTheme,
} from '@mui/material'
import api from '../../api/axiosInstance'
import { getPlans } from '../../api/plan/index'
import Swal from 'sweetalert2'

const STATUS_LABELS = {
  ACTIVE: { label: 'Aktif', emoji: '✅', color: '#7dd3b8' },
  EXPIRED: { label: 'Süresi Doldu', emoji: '⌛', color: '#94a3b8' },
  PAST_DUE: { label: 'Gecikmiş', emoji: '⚠️', color: '#fbbf24' },
  CANCELED: { label: 'İptal', emoji: '⛔', color: '#f87171' },
}

const formatDate = (value) => (value ? String(value).split('T')[0] : '—')

const UserSubscription = ({ userInfo }) => {
  const theme = useTheme()
  const dc = theme.palette.designTokens
  const [plans, setPlans] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [canceling, setCanceling] = useState(false)

  const activeSubscription = subscriptions.find((s) => s.status === 'ACTIVE')

  const fetchData = async () => {
    if (!userInfo?.id) return
    try {
      setLoading(true)
      const [planList, subsResponse] = await Promise.all([
        getPlans(),
        api.get(`/subscriptions/admin/${userInfo.id}`),
      ])
      setPlans(Array.isArray(planList) ? planList : [])
      setSubscriptions(Array.isArray(subsResponse.data) ? subsResponse.data : [])
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Paket bilgileri alınamadı',
        text: error?.response?.data?.message || 'Beklenmeyen bir hata oluştu',
        confirmButtonText: 'Tamam',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [userInfo?.id])

  const handleAssign = async () => {
    if (!selectedPlanId) {
      Swal.fire({
        icon: 'warning',
        title: 'Paket seçilmedi',
        text: 'Lütfen atanacak paketi seçin',
        confirmButtonText: 'Tamam',
      })
      return
    }

    const plan = plans.find((p) => p.id === selectedPlanId)
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Paket atansın mı?',
      text: `"${plan?.name}" paketi ${userInfo.name} kullanıcısına aktif olarak tanımlanacak. Varsa mevcut aktif paketi iptal edilecek.`,
      showCancelButton: true,
      confirmButtonText: 'Evet, ata',
      cancelButtonText: 'Vazgeç',
    })
    if (!confirm.isConfirmed) return

    try {
      setSaving(true)
      await api.post(`/subscriptions/admin/${userInfo.id}`, {
        planId: selectedPlanId,
      })
      Swal.fire({
        icon: 'success',
        title: 'Paket atandı',
        text: 'Kullanıcının paketi aktif hale getirildi',
        confirmButtonText: 'Tamam',
      })
      setSelectedPlanId('')
      fetchData()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Paket atanamadı',
        text: error?.response?.data?.message || 'Beklenmeyen bir hata oluştu',
        confirmButtonText: 'Tamam',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async () => {
    if (!activeSubscription) return

    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Paket geri alınsın mı?',
      text: `"${activeSubscription.plan?.name}" paketi ${userInfo.name} kullanıcısından geri alınacak ve erişimi sonlandırılacak.`,
      showCancelButton: true,
      confirmButtonText: 'Evet, geri al',
      cancelButtonText: 'Vazgeç',
      confirmButtonColor: '#d33',
    })
    if (!confirm.isConfirmed) return

    try {
      setCanceling(true)
      await api.delete(
        `/subscriptions/admin/${userInfo.id}/${activeSubscription.id}`,
      )
      Swal.fire({
        icon: 'success',
        title: 'Paket geri alındı',
        text: 'Kullanıcının aktif paketi iptal edildi',
        confirmButtonText: 'Tamam',
      })
      fetchData()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Paket geri alınamadı',
        text: error?.response?.data?.message || 'Beklenmeyen bir hata oluştu',
        confirmButtonText: 'Tamam',
      })
    } finally {
      setCanceling(false)
    }
  }

  const cardSx = {
    background: dc.card,
    border: `1px solid ${dc.cardBorder}`,
    borderRadius: '20px',
    backdropFilter: 'none',
    p: '28px 32px',
    height: '100%',
  }

  const titleSx = { fontSize: 18, fontWeight: 800, color: '#f1f5f9', mb: '20px' }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: dc.field,
      borderRadius: '10px',
      fontSize: 15,
      color: dc.value,
      '& fieldset': { borderColor: dc.fieldBorder },
      '&:hover fieldset': { borderColor: dc.fieldBorder },
      '&.Mui-focused fieldset': { borderColor: dc.accent, borderWidth: '1px' },
    },
    '& .MuiInputBase-input': { paddingTop: '11px', paddingBottom: '11px' },
  }

  const primaryBtnSx = (disabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    px: '22px',
    py: '12px',
    border: 'none',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    color: dc.onGradient,
    background: dc.gradientBtn,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: disabled ? 'none' : 'translateY(-1px)',
      boxShadow: disabled ? 'none' : '0 4px 14px rgba(16,185,129,0.35)',
    },
  })

  const StatusPill = ({ status }) => {
    const s = STATUS_LABELS[status] || {
      label: status,
      emoji: '•',
      color: '#94a3b8',
    }
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: '10px',
          py: '5px',
          borderRadius: '8px',
          fontSize: 12,
          fontWeight: 700,
          background: dc.chipBg,
          border: `1px solid ${dc.chipBorder}`,
          color: s.color,
        }}
      >
        {s.emoji} {s.label}
      </Box>
    )
  }

  const DetailRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontSize: 13, color: dc.label }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: dc.value }}>
        {value}
      </Typography>
    </Box>
  )

  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress sx={{ color: dc.accent }} />
      </Grid>
    )
  }

  return (
    <Grid container spacing={3}>
      {/* Mevcut paket */}
      <Grid item xs={12} md={6}>
        <Box sx={cardSx}>
          <Typography sx={titleSx}>Mevcut Paket</Typography>

          {activeSubscription ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: dc.accent }}>
                  👑 {activeSubscription.plan?.name}
                </Typography>
                <StatusPill status="ACTIVE" />
              </Box>
              <DetailRow
                label="Başlangıç"
                value={formatDate(activeSubscription.currentPeriodStart)}
              />
              <DetailRow
                label="Bitiş"
                value={formatDate(activeSubscription.currentPeriodEnd)}
              />
              <DetailRow
                label="Monitör limiti"
                value={activeSubscription.plan?.limits?.maxMonitors ?? '—'}
              />
              <DetailRow
                label="En sık kontrol"
                value={`${activeSubscription.plan?.limits?.minIntervalMinutes ?? '—'} dk`}
              />
              <Box
                component="button"
                onClick={handleCancel}
                disabled={canceling}
                sx={{
                  mt: 1.5,
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: '18px',
                  py: '10px',
                  borderRadius: '12px',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: canceling ? 'not-allowed' : 'pointer',
                  opacity: canceling ? 0.5 : 1,
                  color: '#f87171',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.35)',
                  '&:hover': { background: 'rgba(239,68,68,0.14)' },
                }}
              >
                {canceling ? <CircularProgress size={16} color="inherit" /> : '↩️'} Paketi
                Geri Al
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, color: dc.muted }}>
              Kullanıcının aktif bir paketi yok.
            </Typography>
          )}
        </Box>
      </Grid>

      {/* Paket ata */}
      <Grid item xs={12} md={6}>
        <Box sx={cardSx}>
          <Typography sx={titleSx}>Paket Ata</Typography>

          <Typography sx={{ fontSize: 13, color: dc.muted, mb: 2.5 }}>
            Seçilen paket doğrudan aktif olarak tanımlanır, ödeme akışı
            çalıştırılmaz.
          </Typography>

          <TextField
            fullWidth
            select
            hiddenLabel
            size="small"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            sx={{ ...inputSx, mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ fontSize: 17, lineHeight: 1 }}>
                    👑
                  </Box>
                </InputAdornment>
              ),
            }}
          >
            {plans.length === 0 ? (
              <MenuItem disabled value="">
                Tanımlı paket yok
              </MenuItem>
            ) : (
              plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name} — {plan.price} TL / {plan.durationDays} gün
                </MenuItem>
              ))
            )}
          </TextField>

          <Box
            component="button"
            onClick={handleAssign}
            disabled={saving || plans.length === 0}
            sx={primaryBtnSx(saving || plans.length === 0)}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : '➕'} Paketi Ata
          </Box>
        </Box>
      </Grid>

      {/* Abonelik geçmişi */}
      <Grid item xs={12}>
        <Box sx={{ ...cardSx, height: 'auto' }}>
          <Typography sx={titleSx}>Abonelik Geçmişi</Typography>

          {subscriptions.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: dc.muted }}>
              Bu kullanıcı için abonelik kaydı yok.
            </Typography>
          ) : (
            subscriptions.map((subscription) => (
              <Box
                key={subscription.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  py: 1.75,
                  borderBottom: `1px solid ${dc.fieldBorder}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: dc.value, minWidth: 140 }}>
                  {subscription.plan?.name || 'Bilinmeyen paket'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: dc.muted }}>
                  {formatDate(subscription.currentPeriodStart)} →{' '}
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
                <StatusPill status={subscription.status} />
              </Box>
            ))
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default UserSubscription
